import { useQueryClient } from '@tanstack/react-query';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useOverlayOrder } from '@/contexts/OverlayOrderContext';
import { ProposalSurfaceProvider } from '@/contexts/ProposalSurfaceContext';
import { useSlideoutWidth } from '@/contexts/SlideoutWidthContext';
import { StackingLevelProvider } from '@/contexts/StackingContext';
import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';
import {
  type SlideoutEntry,
  useSlideoutStackContext,
} from '@/lib/crm/contexts/SlideoutStackContext';
import { useViewHealthScope } from '@/lib/view-health';

import { useProposalSidebar } from '../../contexts/ActionSidebarContext';
import { useOrg } from '../../contexts/OrgContext';
import type { ProposalDetail } from '../../lib/proposals/core/types';
import { useProposalOptional } from '../../lib/proposals/hooks/useProposal';
import { proposalQueryKeys } from '../../lib/proposals/query/keys';
import { nextProposalId } from '../../lib/proposals/utils/nextProposalId';
import ProposalDetailPanel from './ProposalDetailPanel';
import {
  PROPOSAL_HEADER_CLASS,
  ProposalSidebarErrorBody,
  ProposalSidebarHeader,
  ProposalSidebarHeaderActions,
  ProposalSidebarLoadingBody,
  type ProposalSidebarState,
  ProposalSidebarStateProvider,
  type ProposalSidebarValue,
} from './ProposalSidebarRegions';

// Static slot elements — the regions read everything from context.
const HEADER_SLOT = <ProposalSidebarHeader />;
const HEADER_ACTIONS_SLOT = <ProposalSidebarHeaderActions />;
// The panel, the skeleton, and the error state bring their own gutters. The
// chrome's `md:` variant is zeroed explicitly (tailwind-merge does not treat
// it as conflicting with the bare utility).
const CONTENT_CLASS = 'px-0 md:px-0';

// Sidebar width bounds — match ActionSidebarContext defaults so the resize
// handle drags within the same range the persisted width supports.
// `setSidebarWidth` in ActionSidebarContext clamps to `0.7 * viewport`; mirror
// that here so dragging the resize handle and persisting the width agree on
// the upper bound (otherwise dragging past 0.7 * viewport snaps back).
const PROPOSAL_SIDEBAR_MIN_WIDTH = 400;
const PROPOSAL_SIDEBAR_MAX_WIDTH_FACTOR = 0.7;
// SSR fallback when `window` is unavailable.
const PROPOSAL_SIDEBAR_MAX_WIDTH_FALLBACK = 1200;

function computeSidebarMaxWidth(): number {
  if (typeof window === 'undefined') {
    return PROPOSAL_SIDEBAR_MAX_WIDTH_FALLBACK;
  }
  return Math.floor(window.innerWidth * PROPOSAL_SIDEBAR_MAX_WIDTH_FACTOR);
}

export interface ProposalSidebarProps {
  /** Stack entry this slideout renders. Must be `kind: 'proposal'`. */
  entry: SlideoutEntry;
}

/**
 * Mounted by `SlideoutRenderer` only when the top entry of the slideout
 * stack is a `proposal` — exactly one slideout component exists in the
 * DOM at any moment. The open/close animation is driven by
 * `ActionSidebarContext.isSidebarVisible` so adjacent layout (e.g.
 * ReasoningChat width) animates in parallel with the slide-out.
 *
 * The slideout stack still tracks the full history (URL-synced) so
 * back-navigation works across kinds (entity → proposal → entity), but
 * the proposal's internal state does NOT survive being covered by a
 * higher entry — when revealed again the proposal re-mounts with React
 * Query cache hot, so content paints immediately.
 */
export default function ProposalSidebar({ entry }: ProposalSidebarProps) {
  const proposalId = useMemo(() => {
    const parsed = Number(entry.id);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [entry.id]);

  const {
    closeProposal,
    contextProposalIds,
    sidebarWidth,
    setSidebarWidth,
    isSidebarVisible,
  } = useProposalSidebar();

  // Proposal width is persisted independently (localStorage, via
  // ActionSidebarContext), but the floating composer computes its right offset
  // from the shared SlideoutWidthContext. Mirror the live width into that
  // shared context so the composer offsets by the proposal's ACTUAL width and
  // never overlaps it. Only the topmost slideout renders, so last-writer-wins
  // correctly leaves the context reflecting the visible slideout.
  const { width: sharedSlideoutWidth, setWidth: setSharedSlideoutWidth } =
    useSlideoutWidth();

  // Capture the shared width that was active before this proposal mounted (e.g.
  // an underlying CRM slideout's width). The ref initializer runs on the first
  // render only — before the mirror effect below overwrites the shared context
  // with the proposal's own width — so it holds the pre-proposal value.
  const widthBeforeProposalRef = useRef(sharedSlideoutWidth);

  const { registerPushGuard, unregisterPushGuard } = useSlideoutStackContext();
  useViewHealthScope('overlay', 'proposal-sidebar', isSidebarVisible);
  const { currentOrg } = useOrg();
  const queryClient = useQueryClient();

  // Register with the overlay-order system so this slideout layers above
  // any modals/overlays opened earlier. Passing `entry.id` re-registers
  // when the dispatcher swaps proposals (replaceTop after auto-advance
  // keeps this component instance mounted), so the new proposal bumps
  // above anything opened in between.
  const overlayOrder = useOverlayOrder(entry.id);

  // Track viewport-derived max width so the resize handle stays in sync
  // with `ActionSidebarContext.setSidebarWidth` (which clamps to 0.7 *
  // viewport). Without a reactive listener, widening or narrowing the
  // browser would leave one side using the stale bound.
  const [maxSidebarWidth, setMaxSidebarWidth] = useState<number>(
    computeSidebarMaxWidth
  );
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const handleResize = () => {
      setMaxSidebarWidth(computeSidebarMaxWidth());
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Propagate auto-save guard from ProposalDetailPanel up to the slideout
  // stack context so pushSlideout is intercepted when a save is pending.
  const handleGuardChange = useCallback(
    (guard: ((action: () => void) => boolean) | null) => {
      if (guard) {
        registerPushGuard(guard);
      } else {
        unregisterPushGuard();
      }
    },
    [registerPushGuard, unregisterPushGuard]
  );

  // Clean up the push guard when this slideout unmounts.
  useEffect(() => {
    return () => {
      unregisterPushGuard();
    };
  }, [unregisterPushGuard]);

  // Fetch proposal using React Query hook
  // IMPORTANT: Use actual org ID (not 0 fallback) to ensure query key matches refetch
  const {
    data: action,
    error,
    refetch,
  } = useProposalOptional(
    currentOrg?.id ?? 0,
    currentOrg?.id ? (proposalId ?? undefined) : undefined
  );

  const handleClose = useCallback(() => {
    closeProposal();
  }, [closeProposal]);

  // A failed fetch is a STATE the user sees and can retry or dismiss — it
  // used to close the panel silently, which hid the failure and left the
  // user wondering why nothing opened.
  const retry = useCallback(() => {
    void refetch();
  }, [refetch]);

  // Set data attribute on body when a proposal is open (for CSS-based
  // mobile header hiding).
  useEffect(() => {
    document.body.setAttribute('data-proposal-open', 'true');
    return () => {
      document.body.removeAttribute('data-proposal-open');
    };
  }, []);

  const handleActionUpdate = useCallback(
    (updatedAction: ProposalDetail) => {
      // Update React Query cache with new proposal data
      if (currentOrg?.id && proposalId) {
        queryClient.setQueryData(
          proposalQueryKeys.detail(currentOrg.id, proposalId),
          updatedAction
        );
      }
    },
    [currentOrg?.id, proposalId, queryClient]
  );

  // Derive getNextActionId from context proposal IDs
  const getNextActionId = useCallback(
    (currentId: number): number | null =>
      nextProposalId(contextProposalIds, currentId),
    [contextProposalIds]
  );

  const handleActionComplete = useCallback(() => {
    const completedId = proposalId;
    // Approving or dismissing closes the slideout. It used to advance to the
    // next proposal in the context list instead, which meant finishing one
    // action silently pulled up another the user hadn't chosen to open.
    //
    // The callers still resolve and pass a next-action id (StickyFooter
    // captures it pre-mutation, before the list invalidation removes the row);
    // it is deliberately ignored here. The full-page /actions view
    // (ProposalCenter.handleActionComplete) still advances — only the slideout
    // changed.
    handleClose();

    // Refresh caches in the background (after navigation, mirroring
    // ProposalCenter.handleActionComplete). The StickyFooter success path
    // intentionally skips onUpdate, so without this the detail cache keeps
    // resolution='executing' forever: every chat surface showing the
    // proposal stays stuck on "executing", and reopening it mounts the
    // panel from the stale cache — which polls, "completes" again, and
    // immediately closes the slideout in a loop.
    //
    // The two invalidations are deliberately NOT symmetric:
    //
    // - detail: eager refetch. That is the whole point above — it is what
    //   clears the stuck 'executing' state.
    // - lists: `refetchType: 'none'`. Approval submits in the background
    //   (submitApprovalInBackground), so this runs *before* the resolution has
    //   committed server-side. An eager refetch here returns a list that still
    //   contains the just-approved proposal, resurrecting the row that was
    //   optimistically removed a beat earlier — the disappear → reappear →
    //   disappear flash. Marking the caches stale is enough: the confirmation
    //   paths (submitApprovalInBackground, the reject mutation, WS proposal
    //   events) each refresh the lists once the resolution HAS committed.
    //
    // ProposalCenter.handleActionComplete fixed exactly this in 184d9a65be;
    // this handler was written later as a copy of that code and reintroduced
    // the bug by omitting the flag. Do not "tidy" the asymmetry away.
    if (currentOrg?.id && completedId) {
      void queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.detail(currentOrg.id, completedId),
      });
      void queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.lists(currentOrg.id),
        refetchType: 'none',
      });
    }
  }, [proposalId, handleClose, currentOrg?.id, queryClient]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleWidthChange = useCallback(
    (next: number) => {
      setSidebarWidth(next);
    },
    [setSidebarWidth]
  );

  // Mirror the proposal's live width into the shared SlideoutWidthContext while
  // visible (runs on mount with the persisted width and on every resize) so the
  // floating composer's right offset tracks the proposal's actual width.
  useEffect(() => {
    if (isSidebarVisible) {
      setSharedSlideoutWidth(sidebarWidth);
    }
  }, [isSidebarVisible, sidebarWidth, setSharedSlideoutWidth]);

  // On unmount (proposal closed or covered by a higher slideout), restore the
  // shared width that was active before this proposal took over, so the
  // underlying slideout revealed beneath it (e.g. a resized CRM panel) keeps
  // its own width instead of inheriting the proposal's persisted width.
  useEffect(() => {
    const widthBeforeProposal = widthBeforeProposalRef.current;
    return () => {
      setSharedSlideoutWidth(widthBeforeProposal);
    };
  }, [setSharedSlideoutWidth]);

  // One resolution state for the whole surface. The chrome's header slot and
  // the body each switch on it; neither can drop the chrome's close button.
  const state = useMemo<ProposalSidebarState>(() => {
    if (action) {
      return { status: 'ready', action: action as ProposalDetail };
    }
    if (error) {
      return { status: 'error', error, retry };
    }
    return { status: 'loading' };
  }, [action, error, retry]);

  const surfaceValue = useMemo<ProposalSidebarValue>(
    () => ({ state, close: handleClose }),
    [state, handleClose]
  );

  // Don't render without org (to prevent query key mismatch)
  if (!currentOrg?.id) {
    return null;
  }

  let sidebarContent: ReactNode;
  switch (state.status) {
    case 'loading':
      sidebarContent = <ProposalSidebarLoadingBody />;
      break;
    case 'error':
      sidebarContent = (
        <ProposalSidebarErrorBody error={state.error} retry={state.retry} />
      );
      break;
    case 'ready':
      sidebarContent = (
        <ProposalDetailPanel
          action={state.action}
          onClose={handleClose}
          onUpdate={handleActionUpdate}
          onComplete={handleActionComplete}
          getNextActionId={getNextActionId}
          // The chrome renders the header row (ProposalSidebarHeader) so it
          // exists in every state, not only once the proposal has loaded.
          hideHeader
          onGuardChange={handleGuardChange}
        />
      );
      break;
    default:
      sidebarContent = assertNever(state);
  }

  return (
    <StackingLevelProvider level={overlayOrder}>
      <ProposalSidebarStateProvider value={surfaceValue}>
        <SlideoutMenu
          isOpen={isSidebarVisible}
          onOpenChange={handleOpenChange}
          dialogAriaLabel='Action sidebar'
          // Backdrop is rendered persistently by `SlideoutRenderer` so it
          // survives kind transitions (e.g. layering an entity on top of a
          // proposal). Letting SlideoutMenu draw its own would mean two
          // overlapping dim layers and a flash of un-dimmed page when the
          // top slideout swaps.
          showBackdrop={false}
          resizable
          initialWidth={sidebarWidth}
          minWidth={PROPOSAL_SIDEBAR_MIN_WIDTH}
          maxWidth={maxSidebarWidth}
          onWidthChange={handleWidthChange}
          header={HEADER_SLOT}
          headerActions={HEADER_ACTIONS_SLOT}
          headerClassName={PROPOSAL_HEADER_CLASS}
          contentClassName={CONTENT_CLASS}
        >
          <ProposalSurfaceProvider surface='sidebar'>
            {sidebarContent}
          </ProposalSurfaceProvider>
        </SlideoutMenu>
      </ProposalSidebarStateProvider>
    </StackingLevelProvider>
  );
}

function assertNever(state: never): never {
  throw new Error(`Unhandled proposal sidebar state: ${JSON.stringify(state)}`);
}
